import { ReportTemplateStatus } from 'src/enums'
import {
  IFormSchemaReportTemplateFilters,
  IFormSchemaReportTemplates,
  IReportTemplate,
  IReportTemplateCountsDto,
  IReportTemplateWarningDto,
} from '../generated-types/report-template'
import { queryClient } from '../query-client'
import { reportTemplateQueryKeys } from '../query-keys'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { ITemplateOption } from 'src/components/report-templates/GenerateReportModal'

const baseUrl = (customerId: EntityId) => `customers/${customerId}/report-templates`

export const endpoints = {
  getFormSchemaReportTemplates(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  getReportTemplateById(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}`
  },
  deleteReportTemplateById(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  createReportTemplate(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  updateReportTemplate(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}`
  },
  getFormSchemaReportTemplateCounts(customerId: EntityId) {
    return `${baseUrl(customerId)}/counts`
  },
  changeReportTemplateStatus(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}/change-status`
  },
  generateReport(customerId: EntityId) {
    return `${baseUrl(customerId)}/generate-report`
  },
  previewReport(customerId: EntityId) {
    return `${baseUrl(customerId)}/preview-report`
  },
  getReportTemplatesByFormSchemaVersion(customerId: EntityId) {
    return `${baseUrl(customerId)}/dropdown`
  },
  getReportTemplateWarnings(customerId: EntityId, reportTemplateId: EntityId, formSchemaId: EntityId) {
    return `${baseUrl(customerId)}/${reportTemplateId}/version-diff?formSchemaId=${formSchemaId}`
  },
  upgradeReportTemplate(customerId: EntityId, reportTemplateId: EntityId) {
    return `${baseUrl(customerId)}/${reportTemplateId}/upgrade`
  },
}

export const invalidateReportTemplate = {
  getFormSchemaReportTemplates(customerId: EntityId) {
    queryClient.invalidateQueries(reportTemplateQueryKeys.GET_FORM_SCHEMA_REPORT_TEMPLATES(customerId))
  },

  getReportTemplateById(customerId: EntityId, id?: EntityId) {
    queryClient.invalidateQueries(reportTemplateQueryKeys.GET_REPORT_TEMPLATE_BY_ID(customerId, id))
  },
  getFormSchemaReportTemplateCounts(customerId: EntityId) {
    queryClient.invalidateQueries(reportTemplateQueryKeys.GET_FORM_SCHEMA_REPORT_TEMPLATE_COUNTS(customerId))
  },
  getReportTemplatesByFormSchemaVersion(customerId: EntityId, formSchemaVersionId: EntityId) {
    queryClient.invalidateQueries(
      reportTemplateQueryKeys.GET_REPORT_TEMPLATES_BY_FORM_SCHEMA_VERSION(customerId, formSchemaVersionId),
    )
  },
  getReportTemplateWarnings(customerId: EntityId, reportTemplateId?: EntityId) {
    queryClient.invalidateQueries(reportTemplateQueryKeys.GET_REPORT_TEMPLATE_WARNINGS(customerId, reportTemplateId))
  },
}

export const useGetFormSchemaReportTemplates = (
  customerId: EntityId,
  queryOptions?: IFormSchemaReportTemplateFilters,
) =>
  useGet<IFormSchemaReportTemplates[]>(endpoints.getFormSchemaReportTemplates(customerId), queryOptions, {
    queryKey: reportTemplateQueryKeys.GET_FORM_SCHEMA_REPORT_TEMPLATES(customerId, queryOptions),
  })

export const useGetReportTemplateById = (customerId: EntityId, id: EntityId) =>
  useGet<IReportTemplate>(endpoints.getReportTemplateById(customerId, id), undefined, {
    queryKey: reportTemplateQueryKeys.GET_REPORT_TEMPLATE_BY_ID(customerId, id),
    enabled: !!id,
  })

export const useCreateReportTemplate = (customerId: EntityId) =>
  usePost<IReportTemplate, IReportTemplate>(endpoints.createReportTemplate(customerId))

export const useUpdateReportTemplate = (customerId: EntityId, id: EntityId) =>
  usePut<IReportTemplate, IReportTemplate>(endpoints.updateReportTemplate(customerId, id))

export const useDeleteReportTemplate = (customerId: EntityId) =>
  useDelete(endpoints.deleteReportTemplateById(customerId))

export const useGetFormSchemaReportTemplateCounts = (customerId: EntityId) =>
  useGet<IReportTemplateCountsDto>(endpoints.getFormSchemaReportTemplateCounts(customerId), undefined, {
    queryKey: reportTemplateQueryKeys.GET_FORM_SCHEMA_REPORT_TEMPLATE_COUNTS(customerId),
  })

export const useChangeReportTemplateStatus = (customerId: EntityId, id: EntityId) =>
  usePut<void, { status: ReportTemplateStatus; formSchemaId: EntityId }>(
    endpoints.changeReportTemplateStatus(customerId, id),
  )

export const useGenerateReport = (customerId: EntityId) =>
  usePost<any, object>(endpoints.generateReport(customerId), {}, { responseType: 'arraybuffer' })

export const usePreviewReport = (customerId: EntityId) =>
  usePost<any, object>(endpoints.previewReport(customerId), {}, { responseType: 'arraybuffer' })

export const useGetReportTemplatesByFormSchemaVersion = (
  customerId: EntityId,
  formSchemaVersionId: EntityId,
  formSchemaId: EntityId,
) =>
  useGet<ITemplateOption[]>(
    endpoints.getReportTemplatesByFormSchemaVersion(customerId),
    {
      formSchemaVersionId: formSchemaVersionId,
      formSchemaId: formSchemaId,
    },
    {
      queryKey: reportTemplateQueryKeys.GET_REPORT_TEMPLATES_BY_FORM_SCHEMA_VERSION(customerId, formSchemaVersionId),
      enabled: !!formSchemaVersionId,
    },
  )

export const useGetReportTemplateWarnings = (
  customerId: EntityId,
  reportTemplateId: EntityId,
  formSchemaId: EntityId,
) =>
  useGet<IReportTemplateWarningDto>(
    endpoints.getReportTemplateWarnings(customerId, reportTemplateId, formSchemaId),
    undefined,
    {
      queryKey: reportTemplateQueryKeys.GET_REPORT_TEMPLATE_WARNINGS(customerId, reportTemplateId),
      enabled: !!reportTemplateId && !!formSchemaId,
    },
  )

export const useUpgradeReportTemplate = (customerId: EntityId, reportTemplateId: EntityId) =>
  usePut<void, void>(endpoints.upgradeReportTemplate(customerId, reportTemplateId))
